import { useMemo, useState } from "react";
import { applicationsApi, tasksApi, ApiError } from "@/api";
import type { JobApplication, DrawingSet, LetterSummary, LetterSource } from "@/types";
import { PLANNING_STATUS_OPTIONS, BC_STATUS_OPTIONS } from "@/types";
import { Button, Pill, SelectField, Toast, useToast } from "@/components/ui";
import { applicationStatusTone, decisionTone, humanise } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { DeadlineChipView } from "./JobChips";
import { CreateApplicationDrawer } from "./CreateApplicationDrawer";
import { EditApplicationDrawer } from "./EditApplicationDrawer";
import { AppLetters } from "./AppLetters";
import { PlanWirePanel, isPlanWireRelevant } from "./PlanWirePanel";
import { LetterDetailDrawer } from "./LetterDetailDrawer";

type ClosePrompt = { appId: number; tasks: { id: number; title: string; priority: string; due_date: string | null }[] };

/** Applications tab — actionable (Brief 03). Create, inline status edit on open
 *  apps, and close/reopen with a prompt to cancel linked open tasks. Only status
 *  and closed_at are mutable; other fields stay display-only (no edit endpoint).
 *  Every write re-fetches /detail via onChanged. */
export function ApplicationsTab({
  jobId,
  jobNumber,
  defaultAddress,
  defaultPostcode,
  applications,
  drawingSets,
  letterSummaries,
  conservationArea = false,
  constraintsResolved = false,
  onChanged,
  onError,
}: {
  jobId: number;
  jobNumber: number;
  defaultAddress: string;
  defaultPostcode: string;
  applications: JobApplication[];
  drawingSets: DrawingSet[];
  letterSummaries: LetterSummary[];
  /** The site is in a confirmed conservation area (from job.constraints). Drives the
   *  create drawer's warn-only nudge away from CLOPUD / Prior Approval. */
  conservationArea?: boolean;
  /** The job's constraints lookup completed (lookup_ok). When false, the create drawer
   *  warns that CLOPUD / Prior Approval is being chosen without a checked conservation status. */
  constraintsResolved?: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [editApp, setEditApp] = useState<JobApplication | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [closePrompt, setClosePrompt] = useState<ClosePrompt | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [openLetter, setOpenLetter] = useState<{ id: number; source: LetterSource } | null>(null);
  const [resubApp, setResubApp] = useState<JobApplication | null>(null);
  const [resubChoice, setResubChoice] = useState("0");
  const [linking, setLinking] = useState(false);

  // Status changes (incl. tagging "submitted"), create/close/reopen and linked-task
  // cancellations confirm via the shared transient toast — not the page-level flash
  // banner — so the action's feedback stays anchored to this tab.
  const { toast, showToast } = useToast();

  const lettersById = useMemo(() => {
    const map = new Map<number, LetterSummary>();
    for (const s of letterSummaries) map.set(s.application_id, s);
    return map;
  }, [letterSummaries]);

  // Superseded-app id → the sibling that resubmits it (at most one, server-enforced).
  const supersededBy = useMemo(() => {
    const map = new Map<number, JobApplication>();
    for (const a of applications) {
      const target = a.supersedes_application_id ?? 0;
      if (target > 0) map.set(target, a);
    }
    return map;
  }, [applications]);

  // Siblings offerable as the resubmission: same job (given), planning, not the
  // refused app itself, created after it (a resubmission postdates the refusal),
  // and not already superseding a different application. An app already linked to
  // THIS refusal is always listed so the link stays visible and clearable.
  const resubCandidates = useMemo(() => {
    if (!resubApp) return [];
    return applications.filter(
      (s) =>
        s.id !== resubApp.id &&
        s.type === "planning" &&
        (s.supersedes_application_id === resubApp.id ||
          (((s.supersedes_application_id ?? 0) === 0) &&
            (s.created_at ?? 0) > (resubApp.created_at ?? 0))),
    );
  }, [applications, resubApp]);

  async function changeStatus(app: JobApplication, next: string) {
    if (busyId != null || next === app.status) return;
    setBusyId(app.id);
    try {
      await applicationsApi.setApplicationStatus(app.id, next);
      showToast(`Status: ${humanise(app.status)} → ${humanise(next)}`);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not change the status.");
    } finally {
      setBusyId(null);
    }
  }

  async function close(app: JobApplication) {
    if (busyId != null) return;
    if (!window.confirm(`Close application ${app.app_reference || "(draft)"}? It drops out of deadline tracking.`)) return;
    setBusyId(app.id);
    try {
      const res = await applicationsApi.closeApplication(app.id);
      showToast("Application closed.");
      onChanged();
      if (res.linked_open_tasks_count > 0) {
        setClosePrompt({ appId: app.id, tasks: res.linked_open_tasks });
      }
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not close the application.");
    } finally {
      setBusyId(null);
    }
  }

  async function reopen(app: JobApplication) {
    if (busyId != null) return;
    setBusyId(app.id);
    try {
      await applicationsApi.closeApplication(app.id, true);
      showToast("Application reopened.");
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not reopen the application.");
    } finally {
      setBusyId(null);
    }
  }

  function openResubPrompt(app: JobApplication) {
    setResubChoice(String(supersededBy.get(app.id)?.id ?? 0));
    setResubApp(app);
  }

  async function saveResubmission() {
    if (!resubApp || linking) return;
    setLinking(true);
    try {
      await applicationsApi.setApplicationResubmission(resubApp.id, Number(resubChoice));
      showToast(Number(resubChoice) > 0 ? "Resubmission linked." : "Resubmission link cleared.");
      setResubApp(null);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update the resubmission link.");
    } finally {
      setLinking(false);
    }
  }

  async function cancelLinkedTasks() {
    if (!closePrompt || cancelling) return;
    setCancelling(true);
    try {
      for (const t of closePrompt.tasks) {
        await tasksApi.updateTask({ id: t.id, status: "cancelled" });
      }
      showToast(`Cancelled ${closePrompt.tasks.length} linked task${closePrompt.tasks.length === 1 ? "" : "s"}.`);
      setClosePrompt(null);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not cancel the linked tasks.");
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="job-apps">
      <div className="job-apps__head">
        <p className="job-drawer__muted">{applications.length} application{applications.length === 1 ? "" : "s"}</p>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>New application</Button>
      </div>

      {applications.length === 0 ? (
        <p className="jobs-list__empty">No applications on this job yet. Create one to start tracking it.</p>
      ) : (
        applications.map((app) => (
          <ApplicationCard
            key={app.id}
            app={app}
            letters={lettersById.get(app.id) ?? null}
            busy={busyId === app.id}
            supersededBy={supersededBy.get(app.id) ?? null}
            resubmissionOf={
              (app.supersedes_application_id ?? 0) > 0
                ? applications.find((s) => s.id === app.supersedes_application_id) ?? null
                : null
            }
            onStatus={(next) => changeStatus(app, next)}
            onEdit={() => setEditApp(app)}
            onClose={() => close(app)}
            onReopen={() => reopen(app)}
            onLinkResub={() => openResubPrompt(app)}
            onOpenLetter={(letterId, src) => setOpenLetter({ id: letterId, source: src })}
          />
        ))
      )}

      {showCreate ? (
        <CreateApplicationDrawer
          jobId={jobId}
          jobNumber={jobNumber}
          defaultAddress={defaultAddress}
          defaultPostcode={defaultPostcode}
          drawingSets={drawingSets}
          conservationArea={conservationArea}
          constraintsResolved={constraintsResolved}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            showToast("Application created.");
            onChanged();
          }}
        />
      ) : null}

      {editApp ? (
        <EditApplicationDrawer
          app={editApp}
          jobNumber={jobNumber}
          drawingSets={drawingSets}
          onClose={() => setEditApp(null)}
          onSaved={() => {
            setEditApp(null);
            showToast("Application updated.");
            onChanged();
          }}
        />
      ) : null}

      {closePrompt ? (
        <div className="job-modal__overlay" onClick={cancelling ? undefined : () => setClosePrompt(null)}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Cancel linked tasks">
            <header className="job-modal__head"><h3>Cancel linked tasks?</h3></header>
            <div className="job-modal__body">
              <p className="job-drawer__muted">
                This application had {closePrompt.tasks.length} open task{closePrompt.tasks.length === 1 ? "" : "s"}. Cancel
                {closePrompt.tasks.length === 1 ? " it" : " them"} too, or keep
                {closePrompt.tasks.length === 1 ? " it" : " them"} on the job?
              </p>
              <ul className="job-modal__tasks">
                {closePrompt.tasks.map((t) => (
                  <li key={t.id}>
                    <span className="job-task__title">{t.title}</span>
                    <span className="job-drawer__muted">
                      {humanise(t.priority)}{t.due_date ? ` · due ${formatDateShort(t.due_date)}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <footer className="job-modal__foot">
              <Button variant="ghost" onClick={() => setClosePrompt(null)} disabled={cancelling}>Keep them</Button>
              <Button variant="primary" onClick={cancelLinkedTasks} loading={cancelling}>Cancel these tasks</Button>
            </footer>
          </div>
        </div>
      ) : null}

      {resubApp ? (
        <div className="job-modal__overlay" onClick={linking ? undefined : () => setResubApp(null)}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Link resubmission">
            <header className="job-modal__head"><h3>Link resubmission</h3></header>
            <div className="job-modal__body">
              <p className="job-drawer__muted">
                Pick the application that resubmits {resubApp.app_reference || "this draft"}. The consent
                stats then count the pair as one thread — a multi-attempt approval instead of a refusal
                plus a separate first-time approval.
              </p>
              <SelectField
                label="Resubmission"
                value={resubChoice}
                disabled={linking}
                onChange={(e) => setResubChoice(e.target.value)}
              >
                <option value="0">— none —</option>
                {resubCandidates.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {(s.app_reference || "Draft")
                      + (s.date_submitted ? ` · ${formatDateShort(s.date_submitted)}` : "")
                      + (s.status ? ` · ${humanise(s.status)}` : "")}
                  </option>
                ))}
              </SelectField>
            </div>
            <footer className="job-modal__foot">
              <Button variant="ghost" onClick={() => setResubApp(null)} disabled={linking}>Cancel</Button>
              <Button variant="primary" onClick={saveResubmission} loading={linking}>Save link</Button>
            </footer>
          </div>
        </div>
      ) : null}

      {openLetter ? (
        <LetterDetailDrawer id={openLetter.id} source={openLetter.source} onClose={() => setOpenLetter(null)} />
      ) : null}

      <Toast toast={toast} />
    </div>
  );
}

function statusOptionsFor(type: string | null): readonly string[] {
  return type === "building_control" ? BC_STATUS_OPTIONS : PLANNING_STATUS_OPTIONS;
}

function ApplicationCard({
  app,
  letters,
  busy,
  supersededBy,
  resubmissionOf,
  onStatus,
  onEdit,
  onClose,
  onReopen,
  onLinkResub,
  onOpenLetter,
}: {
  app: JobApplication;
  letters: LetterSummary | null;
  busy: boolean;
  /** The sibling application that resubmits this one (this app is superseded). */
  supersededBy: JobApplication | null;
  /** The sibling application this one resubmits (this app is the newer attempt). */
  resubmissionOf: JobApplication | null;
  onStatus: (next: string) => void;
  onEdit: () => void;
  onClose: () => void;
  onReopen: () => void;
  onLinkResub: () => void;
  onOpenLetter: (id: number, source: LetterSource) => void;
}) {
  const typeLabel = humanise(app.application_type || app.type);
  // Keep the current status selectable even if it's outside the standard list
  // (e.g. the create-time "draft"), so the select reflects reality.
  const baseOptions = statusOptionsFor(app.type);
  const statusOptions = app.status && !baseOptions.includes(app.status) ? [app.status, ...baseOptions] : baseOptions;

  const showPlanWire = isPlanWireRelevant(app);

  return (
    <article className={`job-app${app.is_open ? "" : " job-app--closed"}`}>
      <header className="job-app__head">
        <div className="job-app__title">
          <span className="job-app__type">{typeLabel}</span>
          <span className="job-app__ref numeric">{app.app_reference || "Draft — no reference"}</span>
        </div>
        <div className="job-app__badges">
          <Pill tone={app.is_open ? "info" : "neutral"}>{app.is_open ? "Open" : "Closed"}</Pill>
          {app.is_major ? <Pill tone="accent">Major</Pill> : null}
        </div>
      </header>

      {app.local_authority_or_bcb ? <p className="job-app__lpa">{app.local_authority_or_bcb}</p> : null}
      {app.description ? <p className="job-app__desc">{app.description}</p> : null}

      <div className="job-app__status-row">
        {app.is_open ? (
          <SelectField
            label="Status"
            className="job-app__status-field"
            value={app.status ?? ""}
            disabled={busy}
            onChange={(e) => onStatus(e.target.value)}
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>{humanise(s)}</option>
            ))}
          </SelectField>
        ) : app.status ? (
          <Pill tone={applicationStatusTone(app.is_open)}>{humanise(app.status)}</Pill>
        ) : null}
        {app.decision_outcome ? <Pill tone={decisionTone(app.decision_outcome)}>{humanise(app.decision_outcome)}</Pill> : null}
        {supersededBy ? <Pill tone="neutral">Superseded by {supersededBy.app_reference || "draft"}</Pill> : null}
        {resubmissionOf ? <Pill tone="info">Resubmission of {resubmissionOf.app_reference || "draft"}</Pill> : null}
      </div>

      <dl className="job-app__dates">
        <DateRow label="Submitted" value={app.date_submitted} />
        <DateRow label="Validated" value={app.date_validated} />
        <DateRow label="Decision" value={app.date_decision} />
      </dl>

      {app.next_deadline || app.days_remaining != null ? (
        <div className="job-app__deadline">
          <span className="job-drawer__muted">
            Next deadline: {formatDateShort(app.next_deadline)}
            {app.deadline_type ? ` · ${humanise(app.deadline_type)}` : ""}
          </span>
          <DeadlineChipView daysRemaining={app.days_remaining} />
        </div>
      ) : null}

      {letters && letters.count > 0 ? (
        <div className="job-app__letters">
          <span className="job-app__letters-count">
            {letters.count} letter{letters.count === 1 ? "" : "s"} · {letters.planning_count} planning · {letters.bc_count} BC
          </span>
          {letters.latest ? (
            <span className="job-drawer__muted">
              Latest: {humanise(letters.latest.letter_type) || "—"}
              {letters.latest.letter_subtype ? ` / ${humanise(letters.latest.letter_subtype)}` : ""}
              {letters.latest.letter_date ? ` · ${formatDateShort(letters.latest.letter_date)}` : ""}
              {letters.latest.confidence ? ` · ${humanise(letters.latest.confidence)} confidence` : ""}
            </span>
          ) : null}
          <AppLetters applicationId={app.id} onOpen={onOpenLetter} />
        </div>
      ) : null}

      {showPlanWire ? <PlanWirePanel app={app} /> : null}

      <div className="job-app__actions">
        {app.application_view_url ? (
          <a
            className="job-app__link"
            href={app.application_view_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on council portal ↗
          </a>
        ) : null}
        {app.type === "planning" && app.status === "refused" ? (
          <button type="button" className="job-app__link" disabled={busy} onClick={onLinkResub}>
            {supersededBy ? "Change resubmission…" : "Link resubmission…"}
          </button>
        ) : null}
        {app.is_open ? (
          <>
            <button type="button" className="job-app__link" disabled={busy} onClick={onEdit}>Edit application</button>
            <button type="button" className="job-app__link" disabled={busy} onClick={onClose}>Close application</button>
          </>
        ) : (
          <Button variant="secondary" size="sm" loading={busy} onClick={onReopen}>Reopen</Button>
        )}
      </div>
    </article>
  );
}

function DateRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{formatDateShort(value)}</dd>
    </div>
  );
}
